import React, { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { useToast } from "../hooks/use-toast";
import { contactData } from "../data/mockData";
import {
  Phone,
  MessageSquare,
  Clock,
  CheckCircle,
  Send,
  Calculator,
  FileText,
  Calendar,
  Instagram,
} from "lucide-react";

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    empresa: "",
    tipoEvento: "",
    orcamento: "",
    dataEvento: "",
    mensagem: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

      const payload = {
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        empresa: formData.empresa || null,
        tipo_evento: formData.tipoEvento,
        orcamento: formData.orcamento || null,
        data_evento: formData.dataEvento || null,
        mensagem: formData.mensagem,
      };

      const response = await fetch(`${API}/contatos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast({
          title: "Mensagem enviada com sucesso!",
          description: "Nossa equipe entrará em contato em até 2 horas.",
        });

        setFormData({
          nome: "",
          email: "",
          telefone: "",
          empresa: "",
          tipoEvento: "",
          orcamento: "",
          dataEvento: "",
          mensagem: "",
        });
      } else {
        throw new Error("Erro ao enviar mensagem");
      }
    } catch (error) {
      console.error("Erro ao enviar contato:", error);
      toast({
        title: "Erro ao enviar mensagem",
        description: "Tente novamente ou entre em contato via WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: Phone,
      title: "WhatsApp Principal",
      value: contactData.phone,
      description: "Resposta imediata",
      color: "from-green-500 to-green-600",
      href: `https://wa.me/${contactData.whatsapp}`,
    },
    {
      icon: Phone,
      title: "Telefone Alternativo",
      value: contactData.phone2,
      description: "Segunda opção de contato",
      color: "from-blue-500 to-blue-600",
      href: `tel:${contactData.phone2}`,
    },
    {
      icon: Instagram,
      title: "Instagram",
      value: contactData.instagram,
      description: "Veja nossos projetos",
      color: "from-pink-500 to-purple-600",
      href: "https://instagram.com/forttana_eventos",
    },
  ];

  const serviceTypes = [
    "Stand para Feira Comercial",
    "Exposição Artística/Cultural",
    "Evento Corporativo",
    "Quiosque/PDV",
    "Ambientação/Cenografia",
    "Projeto Personalizado",
  ];

  const budgetRanges = [
    "Até R$ 10.000",
    "R$ 10.000 - R$ 25.000",
    "R$ 25.000 - R$ 50.000",
    "R$ 50.000 - R$ 100.000",
    "Acima de R$ 100.000",
  ];

  return (
    <section id="contato" className="py-20 bg-white overflow-x-clip">
      <div className="mx-auto w-full max-w-7xl px-4 min-w-0">
        {/* Header */}
        <div className="text-center mb-16 min-w-0">
          <Badge className="mb-6 bg-red-50 text-[#FF1616] hover:bg-red-100 px-4 py-2 font-bold tracking-wide">
            ENTRE EM CONTATO
          </Badge>

          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight">
            VAMOS CRIAR SEU{" "}
            <span className="text-[#FF1616] block">PRÓXIMO PROJETO</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-medium">
            Conte sua visão e nós transformaremos em uma experiência
            extraordinária
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 min-w-0">
          {/* Contact Methods */}
          <div className="lg:col-span-1 min-w-0">
            <h3 className="text-2xl font-black text-gray-900 mb-8 tracking-wide">
              FALE CONOSCO
            </h3>

            <div className="space-y-6 min-w-0">
              {contactMethods.map((method, index) => {
                const IconComponent = method.icon;
                return (
                  <Card
                    key={index}
                    className="min-w-0 hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-gray-50 to-white group cursor-pointer"
                  >
                    <CardContent className="p-6 min-w-0">
                      <a
                        href={method.href}
                        className="flex items-start gap-4 min-w-0"
                      >
                        <div
                          className={[
                            "w-12 h-12 shrink-0",
                            `bg-gradient-to-r ${method.color}`,
                            "rounded-xl flex items-center justify-center",
                            "group-hover:scale-110 transition-transform duration-300",
                          ].join(" ")}
                        >
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 mb-1 tracking-wide break-words">
                            {method.title}
                          </h4>

                          {/* ✅ aqui costuma “vazar” (tel/instagram). */}
                          <p className="text-gray-800 font-medium mb-1 break-all">
                            {method.value}
                          </p>

                          <p className="text-sm text-gray-600 break-words">
                            {method.description}
                          </p>
                        </div>
                      </a>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Quick Info */}
            <Card className="mt-8 border-0 bg-gradient-to-br from-red-50 to-red-100 min-w-0">
              <CardContent className="p-6 min-w-0">
                <div className="flex items-center mb-4 min-w-0">
                  <Clock className="w-5 h-5 text-[#FF1616] mr-2 shrink-0" />
                  <h4 className="font-bold text-gray-900 tracking-wide break-words">
                    ATENDIMENTO
                  </h4>
                </div>

                <div className="space-y-2 text-sm text-gray-700 font-medium min-w-0">
                  <div className="break-words">Segunda a Sexta: 8h às 18h</div>
                  <div className="break-words">Sábado: 8h às 12h</div>
                  <div className="break-words">
                    <strong>WhatsApp: 24h</strong> (resposta rápida)
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2 min-w-0">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-gray-50 min-w-0">
              <CardContent className="p-8 min-w-0">
                <div className="flex items-center mb-8 min-w-0">
                  <div className="w-10 h-10 bg-[#FF1616] rounded-lg flex items-center justify-center mr-4 shrink-0">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 tracking-wide break-words">
                    SOLICITE SEU ORÇAMENTO
                  </h3>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 min-w-0">
                  {/* Basic Info */}
                  <div className="grid md:grid-cols-2 gap-6 min-w-0">
                    <div className="min-w-0">
                      <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide">
                        NOME COMPLETO *
                      </label>
                      <Input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        placeholder="Seu nome completo"
                        required
                        className="w-full min-w-0 border-gray-300 focus:border-[#FF1616] focus:ring-[#FF1616]"
                      />
                    </div>

                    <div className="min-w-0">
                      <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide">
                        EMAIL *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="seu@email.com"
                        required
                        className="w-full min-w-0 border-gray-300 focus:border-[#FF1616] focus:ring-[#FF1616]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 min-w-0">
                    <div className="min-w-0">
                      <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide">
                        WHATSAPP *
                      </label>
                      <Input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleInputChange}
                        placeholder="(61) 99999-9999"
                        required
                        className="w-full min-w-0 border-gray-300 focus:border-[#FF1616] focus:ring-[#FF1616]"
                      />
                    </div>

                    <div className="min-w-0">
                      <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide">
                        EMPRESA
                      </label>
                      <Input
                        type="text"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleInputChange}
                        placeholder="Nome da empresa"
                        className="w-full min-w-0 border-gray-300 focus:border-[#FF1616] focus:ring-[#FF1616]"
                      />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="grid md:grid-cols-2 gap-6 min-w-0">
                    <div className="min-w-0">
                      <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide">
                        <Calculator className="w-4 h-4 inline mr-1" />
                        TIPO DE PROJETO *
                      </label>
                      <select
                        name="tipoEvento"
                        value={formData.tipoEvento}
                        onChange={handleInputChange}
                        required
                        className="w-full min-w-0 p-3 border border-gray-300 rounded-lg focus:border-[#FF1616] focus:ring-[#FF1616] bg-white"
                      >
                        <option value="">Selecione o tipo</option>
                        {serviceTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="min-w-0">
                      <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide">
                        <FileText className="w-4 h-4 inline mr-1" />
                        ORÇAMENTO ESTIMADO
                      </label>
                      <select
                        name="orcamento"
                        value={formData.orcamento}
                        onChange={handleInputChange}
                        className="w-full min-w-0 p-3 border border-gray-300 rounded-lg focus:border-[#FF1616] focus:ring-[#FF1616] bg-white"
                      >
                        <option value="">Selecione a faixa</option>
                        {budgetRanges.map((range) => (
                          <option key={range} value={range}>
                            {range}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="min-w-0">
                    <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      DATA DO EVENTO (APROXIMADA)
                    </label>
                    <Input
                      type="date"
                      name="dataEvento"
                      value={formData.dataEvento}
                      onChange={handleInputChange}
                      className="w-full min-w-0 border-gray-300 focus:border-[#FF1616] focus:ring-[#FF1616]"
                    />
                  </div>

                  <div className="min-w-0">
                    <label className="block text-sm font-bold text-gray-700 mb-2 tracking-wide">
                      DETALHES DO PROJETO *
                    </label>
                    <Textarea
                      name="mensagem"
                      value={formData.mensagem}
                      onChange={handleInputChange}
                      placeholder="Conte sobre sua visão, objetivos, público-alvo, local do evento e qualquer detalhe importante..."
                      rows={4}
                      required
                      className="w-full min-w-0 border-gray-300 focus:border-[#FF1616] focus:ring-[#FF1616]"
                    />
                  </div>

                <Button
  type="submit"
  size="lg"
  disabled={isSubmitting}
  className={[
    "w-full",
    "bg-[#FF1616] hover:bg-red-700 text-white",
    "font-black tracking-wide",
    "min-h-[56px] h-auto",        // ✅ remove altura fixa
    "py-4 px-4",                  // ✅ espaço interno suficiente
    "whitespace-normal",          // ✅ permite quebrar linha
    "leading-tight",              // ✅ evita corte vertical
    "text-base sm:text-lg",       // ✅ mobile mais seguro
    "disabled:opacity-50",
  ].join(" ")}
>
  <span className="flex items-center justify-center gap-2 text-center">
    {isSubmitting ? (
      <>
        <CheckCircle className="w-5 h-5 animate-spin shrink-0" />
        <span>ENVIANDO...</span>
      </>
    ) : (
      <>
        <Send className="w-5 h-5 shrink-0" />
        <span>SOLICITAR ORÇAMENTO GRATUITO</span>
      </>
    )}
  </span>
</Button>

                  <p className="text-sm text-gray-600 font-medium text-center mt-2">

  {/* MOBILE */}
  <span className="block sm:hidden">
    📱 Prefere WhatsApp?
    <br />
    <span className="font-semibold">
      Chame agora: {contactData.phone}
    </span>
  </span>

  {/* DESKTOP */}
  <span className="hidden sm:block">
    📱 <strong>Prefere WhatsApp? Chame agora:</strong> {contactData.phone}
  </span>

</p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
